// Vocabulary and lookup tables shared across the app. Ported as-is from
// WEEKLY_2.HTM — no behavior changes in this migration pass.

export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;
export const MEAL_SLOTS = ["breakfast", "lunch", "dinner"] as const;

// Same list as purveyor_types' check constraint (0047) and
// scripts/seed-purveyors.mjs.
export const PURVEYOR_TYPES = ["CSA", "REKO", "Vendor", "Farmers Market", "Farm Stand", "Country Store"];
// products.availability's check constraint (0047).
export const PRODUCT_AVAILABILITY = ["available", "limited", "sold out"];
export const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"];

// Full recipe-type vocabulary (what the dish IS). Beverage and cocktail get a
// row in the day/meal planner just like side/dessert/appetizer. Medicinal and
// preserves are weekly (not day-specific) sections, similar to snacks.
export const RECIPE_TYPES = ["entree", "side", "dessert", "appetizer", "beverage", "cocktail", "medicinal", "preserves", "candy", "other"];
export const PLANNABLE_DISH_TYPES = ["entree", "side", "dessert", "appetizer", "beverage", "cocktail", "other"] as const;

// Sentinel value for the Main/entree slot meaning "we're not cooking this
// meal" -- not a recipe id (guaranteed never to collide with one: recipe
// ids are crypto.randomUUID() or a long random string, see utils.js's
// uid()), so it flows through existing "look up this id, skip if not
// found" code (grocery list, time/nutrition totals) as a no-op for free.
// Persisted as week_plan_meals.is_dining_out (recipe_id null) rather than
// stored literally in the database -- db.js's saveWeekPlanMeal/
// loadWeekPlans etc. translate at the boundary. See
// 0027_dining_out_option.sql.
export const DINING_OUT = "dining-out";

// What a plan slot shows when its recipe id doesn't resolve for the viewer.
// A recipe placed in a plan the viewer can see is normally readable by them
// (0023: planning with a recipe shares it) -- even after its owner leaves the
// household -- so this now appears mainly when the viewer has hidden it with
// "Remove from my library", or a plan/day share that exposed it was revoked.
// The slot itself is kept, so it can be cleared or replaced; the grocery list
// and time totals already skip it.
export const MISSING_RECIPE_LABEL = "Recipe no longer available";
export const SEASONS = ["Spring", "Summer", "Fall", "Winter"];
export const DIET_TAGS = ["vegetarian", "vegan", "glutenFree"];
export const DIET_TAG_LABELS = {
  vegetarian: "Vegetarian",
  vegan: "Vegan",
  glutenFree: "Gluten-free",
};
export const COOKING_METHODS = [
  "Slow Cooker", "Grill", "Air Fryer", "Pressure Cooker", "Oven", "Stovetop", "No-Cook", "Smoker",
  "Sous Vide", "Broiler", "Dutch Oven", "Microwave", "Griddle",
];
export const RECIPE_TYPE_LABELS = {
  entree: "Main",
  side: "Side",
  dessert: "Dessert",
  appetizer: "Appetizer",
  beverage: "Beverage",
  cocktail: "Cocktail",
  medicinal: "Medicinal",
  preserves: "Preserves",
  candy: "Candy",
  other: "Other",
};
export const CATEGORIES = ["produce", "protein", "dairy", "bakery", "bulk", "pantry", "spices", "frozen", "other"];
export const CATEGORY_LABELS = {
  produce: "Produce",
  protein: "Protein",
  dairy: "Dairy",
  bakery: "Bakery",
  bulk: "Bulk (Nuts/Grains/Etc.)",
  pantry: "Pantry",
  spices: "Spices",
  frozen: "Frozen",
  other: "Other",
};
export const UNITS = ["", "g", "kg", "ml", "l", "tsp", "tbsp", "cup", "oz", "lb", "clove", "can", "pinch", "piece(s)", "quart", "gallon", "packet", "dash"];
export const NON_NUMERIC_UNITS = ["to taste", "as needed", "splash"];

// Suggestions (not a fixed vocabulary -- Inventory's unit field is free text,
// unlike recipe units) offered via a datalist: the recipe unit set, plus
// "dozen" (see UNIT_FAMILIES's count family) and a few common selling units
// with no recipe equivalent (Schemas/inventory-schema.md's `unit` field).
export const INVENTORY_UNIT_SUGGESTIONS = [...UNITS.filter((u) => u !== ""), "dozen", "bunch", "pint", "flat", "bag", "each"];

// Unit-conversion vocabulary for grocery-list aggregation (see
// src/lib/groceryUtils.js) -- deliberately scoped to one measurement system
// per family (no cup<->ml or oz<->g) so a converted total never crosses
// from what one recipe actually called for into a different system.
// `toBase` is how many of the family's smallest unit equal one of this
// unit. Units not listed here (count-like units, "", and NON_NUMERIC_UNITS)
// have no family and are left to exact-unit matching.
export const UNIT_FAMILIES = {
  tsp: { family: "us-volume", toBase: 1 },
  tbsp: { family: "us-volume", toBase: 3 },
  cup: { family: "us-volume", toBase: 48 },
  quart: { family: "us-volume", toBase: 192 },
  gallon: { family: "us-volume", toBase: 768 },
  ml: { family: "metric-volume", toBase: 1 },
  l: { family: "metric-volume", toBase: 1000 },
  oz: { family: "us-weight", toBase: 1 },
  lb: { family: "us-weight", toBase: 16 },
  g: { family: "metric-weight", toBase: 1 },
  kg: { family: "metric-weight", toBase: 1000 },
  // Counting units: a blank unit ("3 carrots") and "piece(s)" both mean "this
  // many of it" and used to stay separate lines (56 ingredient names in the
  // live library used both). "dozen" is here for purveyor/inventory
  // quantities; it is not in UNITS, so recipes don't offer it. "clove", "can",
  // "packet", "pinch", "dash" are deliberately NOT count units: a clove is not
  // interchangeable with a whole head of garlic.
  "": { family: "count", toBase: 1 },
  "piece(s)": { family: "count", toBase: 1 },
  dozen: { family: "count", toBase: 12 },
};
export const UNIT_FAMILY_LADDERS = {
  "us-volume": ["tsp", "tbsp", "cup", "quart", "gallon"],
  "metric-volume": ["ml", "l"],
  "us-weight": ["oz", "lb"],
  "metric-weight": ["g", "kg"],
  // Not used to pick a display unit: a count total never auto-promotes to
  // "dozen" ("18" reads better than "1.5 dozen" on a shopping list). See
  // buildGroceryList.
  count: ["", "dozen"],
};
export const PREP_SUGGESTIONS = [
  "diced", "minced", "chopped", "sliced", "grated", "julienned", "crushed",
  "whole", "halved", "quartered", "zested", "peeled", "cubed", "shredded",
];

// Design tokens. This file is the single source of truth, and vite.config.js
// turns it into CSS variables (see src/lib/tokensCss.ts) so index.css never
// repeats a value. This file must stay import-free, and use only TypeScript
// that Node can strip, so the Vite config and the tests can load it in Node.
//
// Colors follow docs/2026-10-03-design-language.md. Each has a light and a
// dark value; components never see either, only COLORS below, whose entries
// are var(--color-*) references, so the theme can change at run time without
// re-rendering. The dark values apply under prefers-color-scheme: dark, or
// when <html data-theme="dark"> is set (data-theme="light" forces light).
type ColorPair = readonly [light: string, dark: string];

// The six accents a person can choose from (the theme belongs to the person,
// not the household). Each is [accent, tint] for light and for dark. Forest
// is the default and is what PALETTE's accent/accentTint hold; any other one
// is applied by <html data-accent="moss"> etc. (see tokensCss.ts and
// src/lib/theme.ts). No orange/amber/rust family: those would read as
// caution or danger.
export const ACCENT_NAMES = ["forest", "moss", "kale", "blueberry", "plum", "beet"] as const;
export type AccentName = (typeof ACCENT_NAMES)[number];
export const DEFAULT_ACCENT: AccentName = "forest";
export const ACCENT_LABELS: Record<AccentName, string> = {
  forest: "Forest",
  moss: "Moss",
  kale: "Kale",
  blueberry: "Blueberry",
  plum: "Plum",
  beet: "Beet",
};
type AccentPair = readonly [accent: string, tint: string];
export const ACCENTS: Record<AccentName, { light: AccentPair; dark: AccentPair }> = {
  forest: { light: ["#33513F", "#E0E8DD"], dark: ["#9CC3A6", "#2B3A30"] },
  moss: { light: ["#56692F", "#E6EAD3"], dark: ["#B7C77E", "#343A22"] },
  kale: { light: ["#2C5F5D", "#DCEAE7"], dark: ["#8CC4BF", "#24393A"] },
  blueberry: { light: ["#34507A", "#DFE6F1"], dark: ["#9DB7E3", "#29344A"] },
  plum: { light: ["#5B3A6B", "#E9E0EE"], dark: ["#C9A6DC", "#382C42"] },
  beet: { light: ["#7A2848", "#F2DEE5"], dark: ["#E59AB5", "#45283A"] },
};

export const PALETTE = {
  paper: ["#F1EBDD", "#1F211C"],
  card: ["#FBF9F3", "#292B25"],
  ink: ["#2B2620", "#EFE8D8"],
  soft: ["#655C4F", "#B5AD9A"],
  line: ["#D3C9B2", "#3E4037"],
  caution: ["#8A4A12", "#E3B26B"],
  danger: ["#9A3B22", "#F0A089"],
  accent: [ACCENTS.forest.light[0], ACCENTS.forest.dark[0]],
  accentTint: [ACCENTS.forest.light[1], ACCENTS.forest.dark[1]],
  onAccent: ["#FBF9F3", "#1F211C"], // text on a filled accent or danger button
} satisfies Record<string, ColorPair>;

// Chip and highlight tints the design language has no place for ("far fewer
// tints than the current set"). The light values are unchanged; the dark ones
// are matched by eye to keep each chip legible at night. The screen slices
// that redraw those chips retire them. Print colors are the same in both:
// printing is always on white paper.
const PENDING_COLORS = {
  amberInk: ["#8A6A1E", "#D9B66A"],
  amberInkDeep: ["#7A5A1E", "#CFAE7A"],
  amberBorder: ["#D9A441", "#8C6A2A"],
  amberTint: ["#F5ECD6", "#3A3322"],
  amberTintSoft: ["#FBF6EA", "#2F2C22"],
  amberTintDeep: ["#EFE6D0", "#3D3526"],
  rustTint: ["#F5DCD5", "#452B24"],
  stoneTint: ["#E3E3D6", "#34362F"],
  clayInk: ["#7A4A32", "#DDAA8E"],
  clayTint: ["#EFE6DC", "#3B302A"],
  skyInk: ["#2B5B75", "#9CC6DE"],
  skyTint: ["#DCEAF2", "#23343F"],
  plumInk: ["#5A3E75", "#C7AEE0"],
  plumTint: ["#EAE3F0", "#352C40"],
  printRule: ["#999", "#999"],
  printInk: ["#000", "#000"],
} satisfies Record<string, ColorPair>;

const ALL_COLORS = { ...PALETTE, ...PENDING_COLORS };
export type ColorName = keyof typeof ALL_COLORS;
const colorNames = Object.keys(ALL_COLORS) as ColorName[];
const kebab = (s: string) => s.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
const byName = <T>(f: (name: ColorName) => T) =>
  Object.fromEntries(colorNames.map((k) => [k, f(k)])) as Record<ColorName, T>;

// { light: { paper: "#F1EBDD", ... }, dark: { ... } } for tokensCss.ts.
export const COLOR_VALUES = {
  light: byName((k) => ALL_COLORS[k][0]),
  dark: byName((k) => ALL_COLORS[k][1]),
};

// What components use: COLORS.accent === "var(--color-accent)".
export type ColorVar = `var(--color-${string})`;
export const COLORS = byName((k): ColorVar => `var(--color-${kebab(k)})`);

// The design language's scale: body 16, supporting text 14, small labels 12
// (nothing smaller), headings 19 to 22, page titles 26 to 30. The old names
// are kept so every use moves up together; several now share a size.
export const FONT_SIZE = {
  xxs: 12,
  xs: 12,
  sm: 14,
  base: 16,
  md: 16,
  lg: 16,
  xl: 18,
  h4: 19,
  h3: 21,
  feature: 25, // the one featured name on a screen (Home's first meal today)
  h2: 22,
  h1: 26,
  display: 30,
};

// xl2 (14) sits between xl and xxl and is used widely in button/card
// padding. 3, 5, 9, 18 and 22 were one-off drift and were snapped to the
// nearest step below (or, for a 4px radius, up to RADIUS.md).
export const SPACE = {
  hair: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  xl2: 14,
  xxl: 16,
  x3: 20,
  x4: 24,
  x5: 32,
};

// Design language: 12 for buttons and inputs, 14 for a tinted block, 22
// (full) for chips and round buttons.
export const RADIUS = {
  md: 12,
  lg: 12,
  xl: 14,
  xxl: 14,
  pill: 22,
};

// Fixed measurements that other spacing is derived from, so an indent is
// written as "the column it lines up under plus the gap" instead of a bare
// number that silently breaks when the column changes. JS-only.
export const LAYOUT = {
  componentIndent: 18, // rows grouped under a recipe component heading
  dishLabelWidth: 66, // "Main"/"Side" label column in plan day cards
  checkSizeMobile: 20, // grocery checkbox on mobile
  tabBarHeight: 64, // phone bottom tab bar, not counting the safe-area inset
  tapTarget: 44, // smallest tap target (design language)
};

// Media queries can't read CSS variables, so this stays JS-only
// (see useIsMobile).
export const BREAKPOINTS = {
  mobile: 700,
};

// From this window width, Plan shows the seven-column week with its picker
// panel (slice 5d); narrower windows get the phone's overview and sheet.
export const WIDE_PLAN_MIN_WIDTH = 1080;

// Loaded from Google Fonts in index.html. Vollkorn is for the names of
// things (titles, recipe and meal names, headings); Atkinson Hyperlegible for
// everything else. Courier is retired, including on the grocery list.
export const FONTS = {
  sans: "'Atkinson Hyperlegible', system-ui, sans-serif",
  serif: "Vollkorn, Georgia, serif",
};
